import { createSlice } from '@reduxjs/toolkit';

import { getPrevNextIds } from '../../helpers/common';

import { DEFAULT_PAGE_SIZE } from '../../constants/common';

import { SLIDER_DEFAULT_TIME_FRAME, SLIDER_DEFAULT_TIME_INTERVAL } from '../../components/TrackLivestock/constants';

import {
  deleteManyAnimals,
  fetchAnimal,
  fetchAnimalTags,
  fetchAnimals,
  fetchAnimalsTags,
  fetchDashboardAnimals,
  fetchStockTypes,
  updateAnimal
} from '../actions/animal';

const animalSlice = createSlice({
  name: 'animal',
  initialState: {
    animal: null,
    animalTags: {},
    calfAry: [],
    count: 0,
    dashboardData: null,
    data: [],
    error: null,
    isTimeSlidersVisible: false,
    loading: false,
    selectedIds: [],
    sireAry: [],
    stockTypes: [],
    sortedData: [],
    tableParams: {
      pagination: { current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0, limit: DEFAULT_PAGE_SIZE, offset: 0 },
      sortOrder: 'ASC',
      sortField: 'id'
    },
    tags: {},
    time_frame: SLIDER_DEFAULT_TIME_FRAME,
    time_interval: SLIDER_DEFAULT_TIME_INTERVAL
  },
  reducers: {
    setSortedData: (state, action) => {
      state.sortedData = action.payload;
    },
    setSelectedIds: (state, action) => {
      state.selectedIds = action.payload;
    },
    setTableParams: (state, action) => {
      state.tableParams = action.payload;
    },
    setTimeSlidersVisibility: (state, action) => {
      state.isTimeSlidersVisible = action.payload;
    },
    setTimeSlidersData: (state, action) => {
      state[action.payload.field] = action.payload.value;
    }
  },
  extraReducers: builder => {
    builder
      // Fetch Animals
      .addCase(fetchAnimals.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnimals.fulfilled, (state, action) => {
        state.calfAry = action.payload.calfAry;
        state.sireAry = action.payload.sireAry;
        state.data = action.payload.data?.records;
        state.count = action.payload.data?.count;
        state.loading = false;
      })
      .addCase(fetchAnimals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Animals for Dashboard
      .addCase(fetchDashboardAnimals.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardAnimals.fulfilled, (state, action) => {
        state.dashboardData = action.payload;
        state.loading = false;
      })
      .addCase(fetchDashboardAnimals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Animal
      .addCase(fetchAnimal.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnimal.fulfilled, (state, action) => {
        const { prev_id, next_id } = state.sortedData?.length
          ? getPrevNextIds(state.sortedData, action.payload?.id)
          : { prev_id: action.payload.prev_id, next_id: action.payload.next_id };
        state.animal = { ...action.payload, prev_id, next_id };
        state.loading = false;
      })
      .addCase(fetchAnimal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Animals Tags
      .addCase(fetchAnimalsTags.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnimalsTags.fulfilled, (state, action) => {
        state.tags = action.payload;
        state.loading = false;
      })
      .addCase(fetchAnimalsTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Animal Tags
      .addCase(fetchAnimalTags.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnimalTags.fulfilled, (state, action) => {
        state.loading = false;
        state.animalTags = action.payload;
      })
      .addCase(fetchAnimalTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Animal Stock Types
      .addCase(fetchStockTypes.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStockTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.stockTypes = action.payload;
      })
      .addCase(fetchStockTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Animal
      .addCase(updateAnimal.pending, (state, action) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(updateAnimal.fulfilled, (state, action) => {
        state.animal = action.payload;
        state.loading = false;
      })
      .addCase(updateAnimal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Many Animals
      .addCase(deleteManyAnimals.pending, (state, action) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(deleteManyAnimals.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteManyAnimals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setSortedData, setSelectedIds, setTableParams, setTimeSlidersData, setTimeSlidersVisibility } =
  animalSlice.actions;
export default animalSlice.reducer;
